import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Switch,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Add,
    Close,
    Delete,
    DragIndicator,
    Email,
    ExpandLess,
    ExpandMore,
    Sms,
    Timer,
    Forum,
} from '@material-ui/icons'
import { toast } from 'react-toastify'
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core'
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import {
    RenewalSequence,
    RenewalSequenceStep,
    SequenceStepType,
} from '../../../models/Concession'
import {
    generateSequenceAiDraft,
    useRenewalSequences,
} from '../../../hooks/useRenewalSequences'
import { validateSequence } from './sequenceValidation'
import { SequenceMergeVariableDropdown } from './SequenceMergeVariableDropdown'
import { RichTextEmailEditor } from '../../Outbound/Campaign/CreateCampaign/RichTextEmailEditor'
import { LoriIcon } from '../../../navigation/layouts/Lori/Icons/LoriIcon'

const BRAND = '#6570E6'

const AI_TONE_PRESETS = [
    'Warm and concise',
    'Friendly',
    'Professional',
    'Urgent',
    'Playful',
]

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            height: '92vh',
            maxHeight: '92vh',
            width: '100%',
            maxWidth: 880,
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: `linear-gradient(180deg, ${BRAND}0a 0%, #ffffff 100%)`,
    },
    headerTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    headerBadge: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: `${BRAND}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    body: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(3),
        backgroundColor: '#fafafa',
    },
    bodyInner: {
        width: '100%',
        maxWidth: 680,
        margin: '0 auto',
    },
    sectionLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    settingsCard: {
        backgroundColor: '#fff',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
    },
    activePill: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: 10,
        backgroundColor: '#f8f9ff',
        border: `1px solid ${BRAND}22`,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#fff',
    },
    // Timeline / step list
    timeline: {
        position: 'relative',
    },
    stepRow: {
        display: 'flex',
        gap: theme.spacing(1.5),
        position: 'relative',
    },
    railCol: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 40,
        flexShrink: 0,
    },
    railNode: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        border: `2px solid ${theme.palette.divider}`,
        zIndex: 1,
    },
    railLine: {
        flex: 1,
        width: 2,
        backgroundColor: theme.palette.divider,
        minHeight: 12,
    },
    stepCard: {
        flex: 1,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        marginBottom: theme.spacing(1.5),
        backgroundColor: '#fff',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
            borderColor: '#d1d5db',
        },
    },
    stepCardDragging: {
        boxShadow: theme.shadows[6],
        opacity: 0.95,
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.5, 1.5),
        cursor: 'pointer',
        userSelect: 'none' as const,
        gap: theme.spacing(1),
    },
    dragHandle: {
        cursor: 'grab',
        display: 'flex',
        alignItems: 'center',
        color: '#c4c7cc',
        '&:active': {
            cursor: 'grabbing',
        },
    },
    stepTitle: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    stepSubtitle: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 320,
    },
    stepContent: {
        padding: theme.spacing(0.5, 2, 2, 2),
    },
    inactiveChip: {
        fontSize: '0.625rem',
        fontWeight: 600,
        padding: '1px 8px',
        borderRadius: 10,
        backgroundColor: '#f3f4f6',
        color: '#9ca3af',
    },
    // Add step
    addStepWrap: {
        display: 'flex',
        gap: theme.spacing(1.5),
    },
    addStepGrid: {
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: theme.spacing(1),
    },
    addStepBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: theme.spacing(1.5, 1),
        borderRadius: 10,
        border: `1px dashed ${theme.palette.divider}`,
        backgroundColor: '#fff',
        cursor: 'pointer',
        transition: 'all 0.12s ease',
        '&:hover': {
            borderColor: BRAND,
            backgroundColor: `${BRAND}08`,
        },
    },
    addStepLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    nameField: {
        '& .MuiInputBase-input': {
            fontWeight: 600,
            fontSize: '1rem',
        },
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    errorBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 8,
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        color: '#b91c1c',
        fontSize: '0.8125rem',
        fontWeight: 500,
        marginBottom: theme.spacing(2),
    },
    mergeRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(-0.5),
    },
    // Lori AI
    loriBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        borderColor: `${BRAND}55`,
        color: BRAND,
        backgroundColor: `${BRAND}0a`,
        '&:hover': {
            borderColor: BRAND,
            backgroundColor: `${BRAND}14`,
        },
    },
    aiDialogPaper: {
        borderRadius: 16,
        overflow: 'hidden',
    },
    aiHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2.5, 3),
        background: `linear-gradient(120deg, ${BRAND} 0%, #8a93f0 100%)`,
        color: '#fff',
    },
    aiHeaderIcon: {
        width: 44,
        height: 44,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255,255,255,0.18)',
        flexShrink: 0,
    },
    aiHeaderTitle: {
        fontWeight: 700,
        fontSize: '1.1rem',
        lineHeight: 1.2,
    },
    aiHeaderSubtitle: {
        fontSize: '0.8rem',
        opacity: 0.92,
    },
    aiFieldLabel: {
        fontWeight: 700,
        fontSize: '0.8rem',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
    },
    toneChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    toneChip: {
        cursor: 'pointer',
        '&:hover': {
            borderColor: BRAND,
            color: BRAND,
        },
    },
    toneChipActive: {
        cursor: 'pointer',
        backgroundColor: `${BRAND}18`,
        color: BRAND,
        borderColor: BRAND,
        fontWeight: 600,
    },
    stepCountRow: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    stepCountChip: {
        cursor: 'pointer',
        minWidth: 40,
    },
}))

const STEP_TYPE_CONFIG: Record<
    SequenceStepType,
    { label: string; icon: React.ReactNode; color: string; hint: string }
> = {
    EMAIL: {
        label: 'Email',
        icon: <Email fontSize="small" />,
        color: '#6570E6',
        hint: 'Rich email via SendGrid',
    },
    SMS: {
        label: 'SMS',
        icon: <Sms fontSize="small" />,
        color: '#1FA971',
        hint: 'Text message via Twilio',
    },
    PORTAL: {
        label: 'Portal',
        icon: <Forum fontSize="small" />,
        color: '#b45309',
        hint: 'In-app portal message',
    },
    DELAY: {
        label: 'Delay',
        icon: <Timer fontSize="small" />,
        color: '#6b7280',
        hint: 'Wait before next step',
    },
}

const STEP_TYPE_ORDER: SequenceStepType[] = ['EMAIL', 'SMS', 'PORTAL', 'DELAY']

interface LocalStep extends RenewalSequenceStep {
    _localId: string
}

const createLocalStep = (type: SequenceStepType, order: number): LocalStep => ({
    _localId: `step-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    step_order: order,
    step_type: type,
    subject: type === 'EMAIL' || type === 'PORTAL' ? '' : undefined,
    content: type !== 'DELAY' ? '' : undefined,
    content_html: type === 'EMAIL' ? '' : undefined,
    delay_days: type === 'DELAY' ? 1 : undefined,
    active: true,
})

const toLocalSteps = (steps: RenewalSequenceStep[]): LocalStep[] =>
    steps.map((s, i) => ({
        ...s,
        _localId: s.id
            ? `existing-${s.id}`
            : `step-${i}-${Math.random().toString(36).slice(2, 8)}`,
    }))

interface Props {
    open: boolean
    sequence?: RenewalSequence
    leasingSeasonId?: number
    onClose: () => void
}

export const SequenceEditorModal: React.FC<Props> = ({
    open,
    sequence,
    leasingSeasonId,
    onClose,
}) => {
    const classes = useStyles()
    const { createSequence, updateSequence } = useRenewalSequences()

    const [name, setName] = useState('')
    const [isActive, setIsActive] = useState(true)
    const [steps, setSteps] = useState<LocalStep[]>([])
    const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set())
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Lori AI generation
    const [aiOpen, setAiOpen] = useState(false)
    const [aiLoading, setAiLoading] = useState(false)
    const [aiNumSteps, setAiNumSteps] = useState(3)
    const [aiTone, setAiTone] = useState('')
    const [aiPrompt, setAiPrompt] = useState('')

    const editorInsertRefs = useRef<Record<string, (text: string) => void>>({})

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    )

    React.useEffect(() => {
        if (open) {
            if (sequence) {
                setName(sequence.name)
                setIsActive(sequence.is_active)
                setSteps(toLocalSteps(sequence.steps))
                setExpandedSteps(new Set())
            } else {
                setName('')
                setIsActive(true)
                setSteps([])
                setExpandedSteps(new Set())
            }
            setErrors({})
            setAiOpen(false)
            setAiLoading(false)
            setAiNumSteps(3)
            setAiTone('')
            setAiPrompt('')
        }
    }, [open, sequence])

    const toggleExpanded = useCallback((localId: string) => {
        setExpandedSteps((prev) => {
            const next = new Set(prev)
            if (next.has(localId)) next.delete(localId)
            else next.add(localId)
            return next
        })
    }, [])

    const updateStep = useCallback(
        (localId: string, patch: Partial<LocalStep>) => {
            setSteps((prev) =>
                prev.map((s) =>
                    s._localId === localId ? { ...s, ...patch } : s,
                ),
            )
        },
        [],
    )

    const removeStep = useCallback((localId: string) => {
        setSteps((prev) => prev.filter((s) => s._localId !== localId))
    }, [])

    const addStep = (type: SequenceStepType) => {
        const newStep = createLocalStep(type, steps.length + 1)
        setSteps((prev) => [...prev, newStep])
        setExpandedSteps((prev) => new Set(prev).add(newStep._localId))
    }

    const handleGenerateAi = async () => {
        setAiLoading(true)
        try {
            const generated = await generateSequenceAiDraft({
                num_steps: aiNumSteps,
                tone: aiTone || undefined,
                prompt: aiPrompt || undefined,
            })

            const localSteps: LocalStep[] = generated.map((s, i) => ({
                ...s,
                _localId: `ai-${Date.now()}-${i}-${Math.random()
                    .toString(36)
                    .slice(2, 6)}`,
            }))

            setSteps(localSteps)
            setExpandedSteps(new Set())
            setErrors({})
            setAiOpen(false)
            toast.success('Lori drafted your sequence — review and save')
        } catch (err) {
            toast.error('Lori could not generate a sequence. Please try again.')
        } finally {
            setAiLoading(false)
        }
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        setSteps((prev) => {
            const oldIndex = prev.findIndex((s) => s._localId === active.id)
            const newIndex = prev.findIndex((s) => s._localId === over.id)
            return arrayMove(prev, oldIndex, newIndex)
        })
    }

    const sortableIds = useMemo(() => steps.map((s) => s._localId), [steps])

    const handleSave = () => {
        const stepsForValidation = steps.map((s, i) => ({
            ...s,
            step_order: i + 1,
        }))

        const validationErrors = validateSequence({
            name,
            is_active: isActive,
            steps: stepsForValidation,
        })

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            // auto-expand the first step that has an error
            const firstErrStep = steps.findIndex(
                (_, i) => validationErrors[`step_${i}`],
            )
            if (firstErrStep >= 0) {
                setExpandedSteps((prev) =>
                    new Set(prev).add(steps[firstErrStep]._localId),
                )
            }
            return
        }

        setErrors({})

        const payload = {
            name,
            is_active: isActive,
            leasing_season: leasingSeasonId ?? null,
            steps: steps.map((s, i) => {
                const base = {
                    step_order: i + 1,
                    step_type: s.step_type,
                    active: s.active,
                }

                switch (s.step_type) {
                    case 'EMAIL':
                        return {
                            ...base,
                            subject: s.subject,
                            content: s.content,
                            content_html: s.content_html,
                        }
                    case 'SMS':
                        return { ...base, content: s.content }
                    case 'PORTAL':
                        return {
                            ...base,
                            subject: s.subject,
                            content: s.content,
                        }
                    case 'DELAY':
                        return { ...base, delay_days: s.delay_days }
                    default:
                        return base
                }
            }),
        }

        if (sequence) {
            updateSequence.mutate(
                { id: sequence.id, body: payload },
                { onSuccess: onClose },
            )
        } else {
            createSequence.mutate(payload, { onSuccess: onClose })
        }
    }

    const isSaving = createSequence.isLoading || updateSequence.isLoading
    const activeStepCount = steps.filter((s) => s.active).length

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                className={classes.dialog}
            >
                {/* Header */}
                <Box className={classes.header}>
                    <Box className={classes.headerTitleRow}>
                        <Box className={classes.headerBadge}>
                            <Forum style={{ color: BRAND }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 700, fontSize: '1.15rem' }}
                            >
                                {sequence ? 'Edit sequence' : 'New sequence'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Automate renewal outreach with a series of
                                messages and delays
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gridGap={8}>
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.loriBtn}
                            startIcon={
                                <LoriIcon
                                    width={16}
                                    height={16}
                                    color={BRAND}
                                />
                            }
                            onClick={() => setAiOpen(true)}
                        >
                            Draft with Lori
                        </Button>
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </Box>

                {/* Body */}
                <Box className={classes.body}>
                    <Box className={classes.bodyInner}>
                        {/* Details card */}
                        <Typography className={classes.sectionLabel}>
                            Details
                        </Typography>
                        <Box className={classes.settingsCard}>
                            <TextField
                                className={classes.nameField}
                                label="Sequence name"
                                placeholder="e.g. Q3 2026 Renewal Drip"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                            <Box className={classes.activePill}>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        style={{ fontWeight: 600 }}
                                    >
                                        Active sequence
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Only one sequence can run at a time.
                                        Active sequences enroll offers as
                                        they&rsquo;re sent.
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={isActive}
                                    onChange={(e) =>
                                        setIsActive(e.target.checked)
                                    }
                                    color="primary"
                                />
                            </Box>
                        </Box>

                        {/* Steps */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <Typography
                                className={classes.sectionLabel}
                                style={{ marginBottom: 0 }}
                            >
                                Steps
                            </Typography>
                            {steps.length > 0 && (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    {steps.length} step
                                    {steps.length === 1
                                        ? ''
                                        : 's'} &middot; {activeStepCount} active
                                </Typography>
                            )}
                        </Box>

                        {errors._steps && (
                            <Box className={classes.errorBanner}>
                                {errors._steps}
                            </Box>
                        )}

                        <Box className={classes.timeline}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={sortableIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {steps.length === 0 && (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            py={5}
                                            px={2}
                                            style={{
                                                border: '2px dashed #e5e7eb',
                                                borderRadius: 12,
                                                marginBottom: 16,
                                                backgroundColor: '#fff',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                Build your sequence
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                align="center"
                                            >
                                                Add message and delay steps
                                                below, or let Lori draft one for
                                                you.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                className={classes.loriBtn}
                                                startIcon={
                                                    <LoriIcon
                                                        width={16}
                                                        height={16}
                                                        color={BRAND}
                                                    />
                                                }
                                                onClick={() => setAiOpen(true)}
                                                style={{ marginTop: 14 }}
                                            >
                                                Draft with Lori
                                            </Button>
                                        </Box>
                                    )}
                                    {steps.map((step, index) => (
                                        <SortableStepCard
                                            key={step._localId}
                                            step={step}
                                            index={index}
                                            isLast={index === steps.length - 1}
                                            expanded={expandedSteps.has(
                                                step._localId,
                                            )}
                                            onToggleExpand={() =>
                                                toggleExpanded(step._localId)
                                            }
                                            onUpdate={(patch) =>
                                                updateStep(step._localId, patch)
                                            }
                                            onRemove={() =>
                                                removeStep(step._localId)
                                            }
                                            onInsertRef={(fn) => {
                                                editorInsertRefs.current[
                                                    step._localId
                                                ] = fn
                                            }}
                                            errors={errors}
                                            classes={classes}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </Box>

                        {/* Add step palette */}
                        <Box mt={1}>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{
                                    display: 'block',
                                    marginBottom: 8,
                                    fontWeight: 600,
                                }}
                            >
                                Add a step
                            </Typography>
                            <Box className={classes.addStepGrid}>
                                {STEP_TYPE_ORDER.map((type) => {
                                    const cfg = STEP_TYPE_CONFIG[type]
                                    return (
                                        <Box
                                            key={type}
                                            className={classes.addStepBtn}
                                            onClick={() => addStep(type)}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: 8,
                                                    backgroundColor: `${cfg.color}15`,
                                                }}
                                            >
                                                {React.cloneElement(
                                                    cfg.icon as React.ReactElement,
                                                    {
                                                        style: {
                                                            color: cfg.color,
                                                            fontSize: 18,
                                                        },
                                                    },
                                                )}
                                            </Box>
                                            <Typography
                                                className={classes.addStepLabel}
                                            >
                                                {cfg.label}
                                            </Typography>
                                        </Box>
                                    )
                                })}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                <Box className={classes.footer}>
                    <Typography variant="caption" color="textSecondary">
                        {steps.length === 0
                            ? 'Add at least one message step to save'
                            : `${steps.length} step${
                                  steps.length === 1 ? '' : 's'
                              } in this sequence`}
                    </Typography>
                    <Box display="flex" gridGap={12}>
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            style={{
                                textTransform: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            disabled={isSaving}
                            startIcon={!isSaving && <Add />}
                            style={{
                                textTransform: 'none',
                                borderRadius: 8,
                                fontWeight: 600,
                                boxShadow: 'none',
                                paddingLeft: 20,
                                paddingRight: 20,
                            }}
                        >
                            {isSaving
                                ? 'Saving...'
                                : sequence
                                ? 'Save changes'
                                : 'Create sequence'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Lori AI draft dialog */}
            <Dialog
                open={aiOpen}
                onClose={() => (aiLoading ? undefined : setAiOpen(false))}
                maxWidth="sm"
                fullWidth
                classes={{ paper: classes.aiDialogPaper }}
            >
                <Box className={classes.aiHeader}>
                    <Box className={classes.aiHeaderIcon}>
                        <LoriIcon width={26} height={26} color="#ffffff" />
                    </Box>
                    <Box flex={1}>
                        <Typography className={classes.aiHeaderTitle}>
                            Draft with Lori
                        </Typography>
                        <Typography className={classes.aiHeaderSubtitle}>
                            Lori will draft a full renewal sequence — messages
                            and delays — for you to review and edit.
                        </Typography>
                    </Box>
                </Box>

                <DialogContent style={{ paddingTop: 24 }}>
                    {steps.length > 0 && (
                        <Box className={classes.errorBanner}>
                            Generating will replace your current {steps.length}{' '}
                            step{steps.length === 1 ? '' : 's'}.
                        </Box>
                    )}

                    <Typography className={classes.aiFieldLabel}>
                        How many message steps?
                    </Typography>
                    <Box className={classes.stepCountRow}>
                        {[2, 3, 4, 5].map((n) => (
                            <Chip
                                key={n}
                                label={n}
                                variant="outlined"
                                size="small"
                                onClick={() => setAiNumSteps(n)}
                                className={`${classes.stepCountChip} ${
                                    aiNumSteps === n
                                        ? classes.toneChipActive
                                        : ''
                                }`}
                            />
                        ))}
                    </Box>

                    <Typography className={classes.aiFieldLabel}>
                        Tone
                    </Typography>
                    <Box className={classes.toneChips}>
                        {AI_TONE_PRESETS.map((preset) => {
                            const active = aiTone === preset
                            return (
                                <Chip
                                    key={preset}
                                    label={preset}
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        setAiTone(active ? '' : preset)
                                    }
                                    className={
                                        active
                                            ? classes.toneChipActive
                                            : classes.toneChip
                                    }
                                />
                            )
                        })}
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Or describe a custom tone…"
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                        style={{ marginBottom: 24 }}
                    />

                    <Typography className={classes.aiFieldLabel}>
                        What should it emphasize?{' '}
                        <span style={{ fontWeight: 400, color: '#9ca3af' }}>
                            (optional)
                        </span>
                    </Typography>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="e.g. Emphasize locking in savings before the offer expires"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                    />
                </DialogContent>

                <DialogActions style={{ padding: '16px 24px' }}>
                    <Button
                        onClick={() => setAiOpen(false)}
                        disabled={aiLoading}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateAi}
                        disabled={aiLoading}
                        variant="contained"
                        color="primary"
                        startIcon={
                            aiLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <LoriIcon
                                    width={18}
                                    height={18}
                                    color="#ffffff"
                                />
                            )
                        }
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            borderRadius: 8,
                        }}
                    >
                        {aiLoading ? 'Lori is writing…' : 'Generate with Lori'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

interface SortableStepCardProps {
    step: LocalStep
    index: number
    isLast: boolean
    expanded: boolean
    onToggleExpand: () => void
    onUpdate: (patch: Partial<LocalStep>) => void
    onRemove: () => void
    onInsertRef: (fn: (text: string) => void) => void
    errors: Record<string, string>
    classes: ReturnType<typeof useStyles>
}

const SortableStepCard: React.FC<SortableStepCardProps> = ({
    step,
    index,
    isLast,
    expanded,
    onToggleExpand,
    onUpdate,
    onRemove,
    onInsertRef,
    errors,
    classes,
}) => {
    const editorInsertRefsLocal = useRef<((text: string) => void) | null>(null)

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: step._localId })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const cfg = STEP_TYPE_CONFIG[step.step_type]
    const stepErrorKey = `step_${index}`
    const hasError = !!errors[stepErrorKey]

    const summary = (() => {
        if (step.step_type === 'DELAY') {
            return step.delay_days
                ? `Wait ${step.delay_days} day${
                      step.delay_days === 1 ? '' : 's'
                  }`
                : cfg.hint
        }
        if (step.subject) return step.subject
        if (step.content) return step.content
        return cfg.hint
    })()

    const handleMergeInsert = (tag: string) => {
        if (step.step_type === 'EMAIL') {
            const insertFn = editorInsertRefsLocal.current
            if (insertFn) {
                insertFn(tag)
            } else {
                onUpdate({ content: (step.content ?? '') + tag })
            }
        } else {
            onUpdate({ content: (step.content ?? '') + tag })
        }
    }

    return (
        <Box className={classes.stepRow}>
            {/* Timeline rail */}
            <Box className={classes.railCol}>
                <Box
                    className={classes.railNode}
                    style={{
                        borderColor: cfg.color,
                        backgroundColor: `${cfg.color}12`,
                    }}
                >
                    {React.cloneElement(cfg.icon as React.ReactElement, {
                        style: { color: cfg.color, fontSize: 18 },
                    })}
                </Box>
                {!isLast && <Box className={classes.railLine} />}
            </Box>

            {/* Step card */}
            <div
                ref={setNodeRef}
                style={style}
                className={`${classes.stepCard} ${
                    isDragging ? classes.stepCardDragging : ''
                }`}
            >
                <Box
                    className={classes.stepHeader}
                    onClick={onToggleExpand}
                    style={
                        hasError
                            ? { boxShadow: 'inset 3px 0 0 #ef4444' }
                            : undefined
                    }
                >
                    <Box
                        {...attributes}
                        {...listeners}
                        className={classes.dragHandle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DragIndicator fontSize="small" />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gridGap={8}>
                            <Typography className={classes.stepTitle}>
                                {index + 1}. {cfg.label}
                            </Typography>
                            {!step.active && (
                                <span className={classes.inactiveChip}>
                                    Paused
                                </span>
                            )}
                        </Box>
                        <Typography className={classes.stepSubtitle}>
                            {summary}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gridGap={2}>
                        <Tooltip
                            title={step.active ? 'Pause step' : 'Enable step'}
                        >
                            <Switch
                                checked={step.active}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    onUpdate({ active: e.target.checked })
                                }}
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                                size="small"
                            />
                        </Tooltip>
                        <Tooltip title="Remove step">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onRemove()
                                }}
                            >
                                <Delete fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                        {expanded ? (
                            <ExpandLess fontSize="small" color="disabled" />
                        ) : (
                            <ExpandMore fontSize="small" color="disabled" />
                        )}
                    </Box>
                </Box>

                <Collapse in={expanded}>
                    <Box className={classes.stepContent}>
                        {step.step_type === 'DELAY' && (
                            <Box
                                display="flex"
                                alignItems="center"
                                gridGap={12}
                            >
                                <TextField
                                    label="Wait"
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    value={step.delay_days ?? ''}
                                    onChange={(e) =>
                                        onUpdate({
                                            delay_days:
                                                parseInt(e.target.value) || 0,
                                        })
                                    }
                                    inputProps={{ min: 1 }}
                                    style={{ width: 110 }}
                                    error={!!errors[`${stepErrorKey}_delay`]}
                                />
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    day(s) before the next step runs
                                </Typography>
                            </Box>
                        )}

                        {step.step_type === 'EMAIL' && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={12}
                            >
                                <TextField
                                    label="Subject"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={step.subject ?? ''}
                                    onChange={(e) =>
                                        onUpdate({ subject: e.target.value })
                                    }
                                    error={!!errors[`${stepErrorKey}_subject`]}
                                    helperText={
                                        errors[`${stepErrorKey}_subject`]
                                    }
                                />
                                <Box className={classes.mergeRow}>
                                    <SequenceMergeVariableDropdown
                                        onSelect={handleMergeInsert}
                                    />
                                </Box>
                                <RichTextEmailEditor
                                    key={step._localId}
                                    htmlValue={step.content_html ?? ''}
                                    onChange={(plain, html) =>
                                        onUpdate({
                                            content: plain,
                                            content_html: html,
                                        })
                                    }
                                    onInsertRef={(fn) => {
                                        editorInsertRefsLocal.current = fn
                                        onInsertRef(fn)
                                    }}
                                    placeholder="Write the email tenants will receive..."
                                />
                                {errors[`${stepErrorKey}_content`] && (
                                    <Typography className={classes.errorText}>
                                        {errors[`${stepErrorKey}_content`]}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {step.step_type === 'SMS' && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={8}
                            >
                                <Box className={classes.mergeRow}>
                                    <SequenceMergeVariableDropdown
                                        onSelect={handleMergeInsert}
                                    />
                                </Box>
                                <TextField
                                    label="Message"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={step.content ?? ''}
                                    onChange={(e) =>
                                        onUpdate({ content: e.target.value })
                                    }
                                    error={!!errors[`${stepErrorKey}_content`]}
                                    helperText={
                                        errors[`${stepErrorKey}_content`] ||
                                        `${
                                            (step.content ?? '').length
                                        } characters`
                                    }
                                />
                            </Box>
                        )}

                        {step.step_type === 'PORTAL' && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={12}
                            >
                                <TextField
                                    label="Subject"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={step.subject ?? ''}
                                    onChange={(e) =>
                                        onUpdate({ subject: e.target.value })
                                    }
                                    error={!!errors[`${stepErrorKey}_subject`]}
                                    helperText={
                                        errors[`${stepErrorKey}_subject`]
                                    }
                                />
                                <Box className={classes.mergeRow}>
                                    <SequenceMergeVariableDropdown
                                        onSelect={handleMergeInsert}
                                    />
                                </Box>
                                <TextField
                                    label="Content"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={step.content ?? ''}
                                    onChange={(e) =>
                                        onUpdate({ content: e.target.value })
                                    }
                                    error={!!errors[`${stepErrorKey}_content`]}
                                    helperText={
                                        errors[`${stepErrorKey}_content`]
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </div>
        </Box>
    )
}
